import { z } from 'zod';

/*
 * The always-CC'd addresses — reference/data-model.md §8, ROADMAP.md §11.3
 * (moved into phase 8).
 *
 * This is the only screen that can put a row in `notification_email_recipient`,
 * and that table is what stands between the send path and somebody actually
 * receiving mail. It is seeded empty on purpose — inventing an address means
 * real notifications going to somebody who does not exist.
 */

export const notificationRecipientInputSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Which address should be told?')
    .max(255)
    .pipe(z.email('That does not look like an email address.')),

  /**
   * "Owner", "Office", "Sam's phone".
   *
   * Optional, and worth having: a screen listing three bare addresses tells
   * nobody whether the fourth is missing.
   */
  label: z.string().trim().max(128).optional(),
});

export type NotificationRecipientInput = z.infer<typeof notificationRecipientInputSchema>;

export const updateNotificationRecipientSchema = z.object({
  label: z.string().trim().max(128).nullable().optional(),
  isActive: z.boolean().optional(),
});

export type UpdateNotificationRecipientInput = z.infer<typeof updateNotificationRecipientSchema>;

export interface NotificationRecipientSummary {
  id: string;
  email: string;
  label: string | null;
  isActive: boolean;
  createdAt: string;
}

/**
 * What each notification type means, in the office's language.
 *
 * On the screen beside the list, because "who gets told" is only answerable if
 * somebody knows what they would be told about.
 */
export const ADMIN_NOTIFICATION_SUMMARY = [
  'A client asks for a clean',
  'Somebody requests a quote or sends a message from the website',
  'Somebody applies for a job',
  'A calendar sync fails',
  'Somebody is still clocked in after sixteen hours',
] as const;
